// Checks on CSV files before they are sent to the reporting service. The
// service validates everything again; these catch the common mistakes
// without an upload.
import Papa from 'papaparse';
import { REPORT_LIMITS } from './reportApi';

// Enough of the file to see the header row and whether data follows it.
const SAMPLE_BYTES = 64 * 1024;

export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const readText = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsText(blob);
  });

// Checks the beginning of a CSV text. Returns a problem description or null.
export const findCsvProblem = (name, text, { isComplete = true } = {}) => {
  // Binary formats (for example an Excel workbook renamed to .csv) contain NUL bytes
  if (text.includes('\u0000')) {
    return `${name} is not a text CSV file. Export the data as CSV and try again.`;
  }
  if (text.trim() === '') {
    return `${name} is empty.`;
  }

  const parsed = Papa.parse(text, { skipEmptyLines: true, preview: 2 });
  const rows = parsed.data || [];
  const header = (rows[0] || []).map((cell) => String(cell).trim().toLowerCase());

  const missing = REPORT_LIMITS.requiredColumns.filter((column) => !header.includes(column.toLowerCase()));
  if (missing.length > 0) {
    return `${name} is missing required column(s): ${missing.join(', ')}.`;
  }
  if (rows.length < 2 && isComplete) {
    return `${name} has a header row but no data rows.`;
  }
  return null;
};

// Validates a file chosen from the user's device. Resolves with a problem
// description, or null when the file looks usable.
export const validateCsvFile = async (file) => {
  if (!/\.csv$/i.test(file.name)) {
    return `${file.name} is not a CSV file. Choose files that end in .csv.`;
  }
  if (file.size === 0) {
    return `${file.name} is empty.`;
  }
  if (file.size > REPORT_LIMITS.maxFileBytes) {
    return `${file.name} is ${formatFileSize(file.size)}. The reporting service accepts files up to ${formatFileSize(REPORT_LIMITS.maxFileBytes)}.`;
  }

  try {
    const sample = await readText(file.slice(0, SAMPLE_BYTES));
    return findCsvProblem(file.name, sample, { isComplete: file.size <= SAMPLE_BYTES });
  } catch (e) {
    return `${file.name} could not be read. Try selecting it again.`;
  }
};

// Two picks of the same file from disk
export const isSameFile = (a, b) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
